import { c as _c } from "react/compiler-runtime";
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import type { StudentApplication, StudentStage } from '@openclassrooms/sdk-web';
import { useStudentStage } from '@openclassrooms/data-connect-web/studentApplication';
import { useFeatureFlags } from '@openclassrooms/utils/hooks/feature-flags';
import { helpSession } from '@openclassrooms/common-app/contact/services';
import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';
import { translate } from '@openclassrooms/translations';
import Stack from '@classify/react-core-components/Stack';
import { APLOwnerContactCard } from '../APLOwnerContactCard';
import Companion from './channels/Companion';
import ButtonPhone from './channels/ButtonPhone';
import ButtonExternalForm from './channels/ButtonExternalForm';
import ButtonMail from './channels/ButtonMail';
import ButtonExternalFormEmployer from './channels/ButtonExternalFormEmployer';
import ButtonFaq from './channels/ButtonFaq';
import RecommendedStudent from './channels/RecommendedStudent';
import RecommendedEmployer from './channels/RecommendedEmployer';
import PageHeader from './PageHeader';
import LoadingState from './LoadingState';
import Footer from './Footer';
import TitleSeparator from './TitleSeparator';
import { type HelpType } from './ContactProvider/useHelpTypeFromPathname';
import { type PageType } from './ContactProvider/useDialog';
const FORCE_DISABLE_EMAIL_BUTTON = true;
type Props = {
  onChangePage: (page: PageType) => void;
  helpType: HelpType;
  onSwitchHelpType: () => void;
  onSendMessage?: (message: string) => void;
  aplOwner?: StudentApplication['owner'];
  supportChannel?: StudentApplication['supportChannel'];
  isOwnerLoading: boolean;
};
function useCanDisplayEmailButton() {
  const $ = _c(8);
  const {
    isHeaderContactPanelEnabled
  } = useFeatureFlags();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["applicant", "candidate", "prospect"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const canUseEmailButton = t0;
  const {
    userId,
    isPending
  } = useCurrentUserContext();
  const t1 = Number(userId);
  let t2;
  if ($[1] !== t1) {
    t2 = {
      userId: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = Boolean(userId);
  let t4;
  if ($[3] !== t3) {
    t4 = {
      enabled: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data
  } = useStudentStage(t2, t4);
  if (!userId && !isPending) {
    return false;
  }
  if (!data) {
    return;
  }
  let t5;
  if ($[5] !== data.studentStage || $[6] !== isHeaderContactPanelEnabled) {
    t5 = canUseEmailButton.includes(data.studentStage) && isHeaderContactPanelEnabled() && !FORCE_DISABLE_EMAIL_BUTTON;
    $[5] = data.studentStage;
    $[6] = isHeaderContactPanelEnabled;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function useCanDisplayPhoneButton(helpType) {
  const {
    getLocalisationConfig
  } = useLocalisationConfig();
  const {
    isConnected,
    isPending
  } = useCurrentUserContext();
  const hasPhoneNumber = Boolean(getLocalisationConfig("contact.phone.number"));
  const isEmployerHelp = helpType === "employer";
  if (!hasPhoneNumber) {
    return false;
  }
  if (isPending) {
    return;
  }
  return isEmployerHelp || isConnected;
}
function useGetChannels(helpType, aplOwner, supportChannel, isOwnerLoading, onChangePage, onSendMessage) {
  const $ = _c(32);
  const shouldDisplayEmailButton = useCanDisplayEmailButton();
  const shouldDisplayPhoneButton = useCanDisplayPhoneButton(helpType);
  const {
    getLocalisationConfig
  } = useLocalisationConfig();
  const isLoading = shouldDisplayEmailButton === undefined || shouldDisplayPhoneButton === undefined || isOwnerLoading;
  let t0;
  if ($[0] !== getLocalisationConfig) {
    t0 = helpSession.hasStarted() && getLocalisationConfig("contact.companion.activated");
    $[0] = getLocalisationConfig;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const shouldShowCompanion = t0;
  const shouldShowAPLOwnerContactCard = helpType === "student" && aplOwner?.name && !isOwnerLoading;
  let channels;
  if ($[2] !== aplOwner || $[3] !== getLocalisationConfig || $[4] !== helpType || $[5] !== onChangePage || $[6] !== onSendMessage || $[7] !== shouldDisplayEmailButton || $[8] !== shouldDisplayPhoneButton || $[9] !== shouldShowAPLOwnerContactCard || $[10] !== shouldShowCompanion || $[11] !== supportChannel) {
    channels = [];
    if (shouldShowCompanion) {
      let t1;
      if ($[13] !== onSendMessage) {
        t1 = <Companion key="companion" onSendMessage={onSendMessage} />;
        $[13] = onSendMessage;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      channels.push(t1);
    }
    if (shouldShowAPLOwnerContactCard) {
      let t1;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <TitleSeparator key="sep1" sx={{
          pt: 2
        }}>{translate("contact.apl_owner.title")}</TitleSeparator>;
        $[15] = t1;
      } else {
        t1 = $[15];
      }
      let t2;
      if ($[16] !== aplOwner || $[17] !== supportChannel) {
        t2 = <>{t1}<APLOwnerContactCard contact={aplOwner} supportChannel={supportChannel} shouldDisplayPrimaryActionButton={true} /></>;
        $[16] = aplOwner;
        $[17] = supportChannel;
        $[18] = t2;
      } else {
        t2 = $[18];
      }
      channels.push(t2);
    }
    let t1;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <TitleSeparator key="sep1" sx={{
        pt: 2
      }}>{translate("global.action.contact-us")}</TitleSeparator>;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    channels.push(t1);
    if (shouldDisplayPhoneButton) {
      let t2;
      if ($[20] !== onChangePage) {
        t2 = <ButtonPhone key="phone" onClick={() => onChangePage("phone")} />;
        $[20] = onChangePage;
        $[21] = t2;
      } else {
        t2 = $[21];
      }
      channels.push(t2);
    }
    if (helpType === "employer") {
      let t2;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <ButtonExternalFormEmployer key="external-form-employer" />;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      channels.push(t2);
      if (!shouldShowCompanion) {
        let t3;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <RecommendedEmployer key="recommended-employer" />;
          $[23] = t3;
        } else {
          t3 = $[23];
        }
        channels.push(t3);
      }
    } else {
      if (shouldDisplayEmailButton) {
        let t2;
        if ($[24] !== onChangePage) {
          t2 = <ButtonMail key="email" onClick={() => onChangePage("email")} />;
          $[24] = onChangePage;
          $[25] = t2;
        } else {
          t2 = $[25];
        }
        channels.push(t2);
      } else {
        if (getLocalisationConfig("contact.genericForm.showInContactPanel")) {
          let t2;
          if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
            t2 = <ButtonExternalForm key="generic-form" />;
            $[26] = t2;
          } else {
            t2 = $[26];
          }
          channels.push(t2);
        }
      }
      if (getLocalisationConfig("faq.student.url") && !shouldShowCompanion) {
        let t2;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <ButtonFaq key="faq" />;
          $[27] = t2;
        } else {
          t2 = $[27];
        }
        channels.push(t2);
      }
      if (!shouldShowCompanion) {
        let t2;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <RecommendedStudent key="recommended" />;
          $[28] = t2;
        } else {
          t2 = $[28];
        }
        channels.push(t2);
      }
    }
    $[2] = aplOwner;
    $[3] = getLocalisationConfig;
    $[4] = helpType;
    $[5] = onChangePage;
    $[6] = onSendMessage;
    $[7] = shouldDisplayEmailButton;
    $[8] = shouldDisplayPhoneButton;
    $[9] = shouldShowAPLOwnerContactCard;
    $[10] = shouldShowCompanion;
    $[11] = supportChannel;
    $[12] = channels;
  } else {
    channels = $[12];
  }
  let t1;
  if ($[29] !== channels || $[30] !== isLoading) {
    t1 = {
      isLoading,
      channels
    };
    $[29] = channels;
    $[30] = isLoading;
    $[31] = t1;
  } else {
    t1 = $[31];
  }
  return t1;
}
export default function PageMain(t0) {
  const $ = _c(12);
  const {
    onChangePage,
    helpType,
    onSwitchHelpType,
    onSendMessage,
    aplOwner,
    supportChannel,
    isOwnerLoading
  } = t0;
  const {
    isLoading,
    channels
  } = useGetChannels(helpType, aplOwner, supportChannel, isOwnerLoading, onChangePage, onSendMessage);
  if (helpType === "none") {
    return null;
  }
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LoadingState />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== helpType || $[2] !== onSwitchHelpType) {
    t1 = <PageHeader type={helpType} onSwitchHelpType={onSwitchHelpType} />;
    $[1] = helpType;
    $[2] = onSwitchHelpType;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== channels) {
    t2 = <Stack spacing={2}>{channels}</Stack>;
    $[4] = channels;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== helpType) {
    t3 = <Footer type={helpType} />;
    $[6] = helpType;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <Stack spacing={4}>{t1}{t2}{t3}</Stack>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}